import { useCallback, useMemo, useState } from 'react';

import { readPersistedFilters, useListFilterPersistence } from '@futrli/predict/utils/list-filters';
import { ProcessedUserRow } from '@futrli/views/processors/users/usersList';

const USERS_STORAGE_KEY = 'futrli:users-list-filters';

interface UsersListFilters {
  roleFilterValue: string;
  statusFilterValue: string;
}

const matchesSearch = (user: ProcessedUserRow, searchTerm: string): boolean => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) {
    return true;
  }

  return (
    user.displayName.toLowerCase().includes(normalizedSearchTerm) ||
    user.email_address.toLowerCase().includes(normalizedSearchTerm)
  );
};

const matchesRole = (user: ProcessedUserRow, roleFilterValue: string): boolean => {
  return roleFilterValue === 'all' || user.role.id === roleFilterValue;
};

const matchesStatus = (user: ProcessedUserRow, statusFilterValue: string): boolean => {
  if (statusFilterValue === 'all') {
    return true;
  }

  return user.status === statusFilterValue;
};

export const useUsersFilters = (users: ProcessedUserRow[], workspaceId: string) => {
  const [searchTerm, setSearchTerm] = useState('');
  const persisted = readPersistedFilters(USERS_STORAGE_KEY, workspaceId);

  const [roleFilterValue, setRoleFilterValue] = useState(() => {
    return (persisted?.filters?.roleFilterValue as string) ?? 'all';
  });
  const [statusFilterValue, setStatusFilterValue] = useState(() => {
    return (persisted?.filters?.statusFilterValue as string) ?? 'all';
  });

  const onRoleFilterChange = useCallback((value: string) => setRoleFilterValue(value), []);
  const onSearchChange = useCallback((value: string) => setSearchTerm(value), []);
  const onStatusFilterChange = useCallback((value: string) => setStatusFilterValue(value), []);

  const filters = useMemo<UsersListFilters>(
    () => ({ roleFilterValue, statusFilterValue }),
    [roleFilterValue, statusFilterValue]
  );

  const handleRestore = useCallback((restoredFilters: UsersListFilters) => {
    setRoleFilterValue(restoredFilters.roleFilterValue ?? 'all');
    setStatusFilterValue(restoredFilters.statusFilterValue ?? 'all');
  }, []);

  useListFilterPersistence({
    storageKey: USERS_STORAGE_KEY,
    organisationId: workspaceId,
    filters,
    sort: undefined,
    onRestore: handleRestore
  });

  const filteredUsers = useMemo(
    () =>
      users.filter(
        user =>
          matchesSearch(user, searchTerm) &&
          matchesRole(user, roleFilterValue) &&
          matchesStatus(user, statusFilterValue)
      ),
    [roleFilterValue, searchTerm, statusFilterValue, users]
  );

  return {
    filteredUsers,
    onRoleFilterChange,
    onSearchChange,
    onStatusFilterChange,
    roleFilterValue,
    searchTerm,
    statusFilterValue
  };
};
